/**
 * Der SSE-Strom auf `GET /api/events`: haelt den Store aktuell, ohne zu pollen.
 *
 * ================= ABWEICHUNG VOM BRIEF: kein `EventSource` (Befund 1 des Task-8-Berichts) =======
 *
 * Der Brief verlangt "oeffnet `EventSource` auf `/api/events` mit `x-client-id`". Das ist mit dem
 * gebauten Backend nicht umsetzbar:
 *
 *   - `EventSource` kennt KEINE eigenen Kopfzeilen. Der Konstruktor nimmt `url` und
 *     `{ withCredentials }` - mehr nicht. Weder `x-client-id` noch `Authorization` liessen sich
 *     mitgeben.
 *   - `GET /api/events` haengt aber an `authenticate` und verlangt `Authorization: Bearer <token>`
 *     im Header (`backend/src/http/middleware/authenticate.ts`). Cookie-Authentifizierung gibt es
 *     nicht, und einen `?session=`-Query liest `authenticate` nicht (anders als der `fileUrl`-
 *     Behelf im Bestand, `index.html:5330`).
 *
 * Ein woertlich gebautes `new EventSource("/api/events")` bekaeme also IMMER 401 und liefe in eine
 * Endlosschleife aus Neuverbindungsversuchen - `EventSource` verbindet nach einem Fehler von sich
 * aus neu und laesst sich dabei nicht bremsen.
 *
 * Deshalb: derselbe Vertrag (Pfad, beide Kopfzeilen, `data:`-Bloecke, Kommentarzeilen als
 * Heartbeat, Wiederaufbau mit wachsendem Abstand), aber ueber `fetch` + `ReadableStream` statt
 * ueber `EventSource`. Faellt die Annahme - etwa weil das Backend Token und Kennung kuenftig als
 * Query-Parameter akzeptiert -, aendert sich ausschliesslich `verbinden()` in dieser Datei.
 *
 * LUECKENAUSGLEICH (2026-10-24): der Server nummeriert jedes Ereignis (`id: <prozess>.<n>`), der
 * Client schickt die zuletzt gesehene Nummer bei jedem (Wieder-)Aufbau als `last-event-id` zurueck.
 * Der Server liefert dann Verpasstes nach und schliesst mit `event: stand`, oder er meldet
 * `event: luecke` (Neustart, Puffer zu klein, zu viel nachzuliefern): dann ruft der Client
 * `aufLuecke`. Nachgeliefertes laeuft ueber denselben `aufEreignis` wie Live-Ereignisse. Ohne
 * bekannte Nummer entscheidet der Server nichts - der Client nimmt die Luecke selbst an, wenn er
 * nach einer ersten Verbindung (oder als uebernehmender Tab) ohne Nummer neu aufbaut, oder wenn die Verbindung
 * davor HTTP 200 bekam, aber weder Stand noch Luecke brachte (ein alter Server nach einem Rueckrollen).
 * Bewusst NICHT gebaut: Mehrfachinstanz-Faehigkeit des Servers (siehe `sse/broadcast.ts`).
 */
import { SitzungsFehler, setzeSitzungsToken, standardKopfzeilen } from "./basis.ts";
import type { SseEreignis } from "../state/reduzierer.ts";

export type { SseEreignis };

/** Heartbeat-Vorgabe des Backends (`events.ts`, `STANDARD_HEARTBEAT_MS`). */
export const HEARTBEAT_MS = 20_000;

/**
 * Wie lange ohne ein einziges Byte gewartet wird, bevor die Verbindung als tot gilt. Zweieinhalb
 * Heartbeat-Perioden: ein einzelner ausgefallener oder verzoegerter Heartbeat (Reverse-Proxy,
 * schlafender Tab) reisst die Verbindung damit noch nicht ab, zwei ausgefallene schon.
 */
export const HEARTBEAT_TOLERANZ_MS = 2.5 * HEARTBEAT_MS;

export const ERSTER_ABSTAND_MS = 1_000;
export const GROESSTER_ABSTAND_MS = 30_000;

/**
 * Abstand bis zum naechsten Verbindungsversuch: verdoppelt sich je Fehlversuch, gedeckelt bei
 * `GROESSTER_ABSTAND_MS`. `fehlversuch` zaehlt ab 1.
 *
 * Bewusst OHNE Zufallsstreuung ("Jitter"): der Bestand hat genau einen Client je Nutzer, es gibt
 * keinen Herdeneffekt abzufedern - und eine zufaellige Komponente waere hier nur eine Zusicherung,
 * die sich nicht mehr auf einen Wert festnageln laesst.
 */
export function naechsterAbstand(fehlversuch: number): number {
  const stufe = Math.max(1, Math.floor(fehlversuch));
  // 2 ** (stufe - 1) waechst schnell ueber jede Zahl hinaus; Math.min deckelt VOR der Multiplikation
  // nicht, deshalb erst rechnen, dann deckeln - bei sehr grossen Stufen ist das Produkt Infinity,
  // und Math.min(GROESSTER, Infinity) ist weiterhin GROESSTER.
  return Math.min(GROESSTER_ABSTAND_MS, ERSTER_ABSTAND_MS * 2 ** (stufe - 1));
}

/**
 * Zerlegt EINEN durch eine Leerzeile abgeschlossenen SSE-Block in ein Ereignis.
 *
 * Liefert `null` fuer alles, was kein verwertbares Ereignis ist - insbesondere fuer die
 * Kommentarzeile `:` (der Heartbeat des Backends, `events.ts:68`), fuer kaputtes JSON und fuer
 * eine Nutzlast, der eines der vier Pflichtfelder fehlt oder die den falschen Typ liefert. Ein
 * fehlgeformtes Ereignis darf den Strom nicht abreissen lassen und schon gar nicht als
 * `{ entity: undefined }` in den Reduzierer laufen.
 *
 * `orderId` (Auftrag "Nachlader-Vier", Hindernis 1): ein FUENFTES, optionales Feld - die vier
 * bestellungsgebundenen Entitaeten tragen es, die uebrigen sechs nicht (`events.ts`, `"orderId" in
 * event`). Bis zu diesem Fund nahm die Destrukturierung unten NUR die vier bekannten Namen mit und
 * warf `orderId` an der Tuer weg, obwohl es ueber die Leitung ankam - der Reduzierer haette dann nie
 * erfahren koennen, zu welcher Bestellung eine Unterressource gehoert.
 */
export function zerlegeSseBlock(block: string): SseEreignis | null {
  return ereignisAus(felderLesen(block).daten);
}

/** Was der Strom liefert: ein nummeriertes Ereignis, den Stand oder die Luecke (Leitungsformat: Kopfkommentar). */
export type SseMeldung =
  | { art: "ereignis"; ereignis: SseEreignis; nummer: string | null }
  | { art: "stand"; nummer: string }
  | { art: "luecke"; nummer: string };

/**
 * Zerlegt EINEN SSE-Block in eine Meldung: ohne `event:` ein Ereignis wie bei `zerlegeSseBlock` (mit der
 * Nummer aus `id:`, falls eine da ist), mit `event: stand|luecke` samt `id:` den Stand bzw. die Luecke
 * (eine `data:`-Zeile daneben zaehlt nicht). Alles andere ist `null`: Heartbeat, kaputtes JSON, ein
 * Stand ohne Nummer, ein unbekannter Meldungsname.
 */
export function zerlegeSseMeldung(block: string): SseMeldung | null {
  const { id, event, daten } = felderLesen(block);
  if (event === "stand" || event === "luecke") return id ? { art: event, nummer: id } : null;
  if (event) return null;
  const ereignis = ereignisAus(daten);
  return ereignis ? { art: "ereignis", ereignis, nummer: id } : null;
}

/** Die drei Felder, die der Strom benutzt; `data:` darf mehrzeilig sein, bei `id:`/`event:` gewinnt die letzte Zeile. */
function felderLesen(block: string): { id: string | null; event: string | null; daten: string[] } {
  const daten: string[] = [];
  let id = "";
  let event = "";
  for (const zeile of block.replace(/\r\n/g, "\n").split("\n")) {
    if (!zeile || zeile.startsWith(":")) continue; // Leerzeile oder Kommentar (= Heartbeat)
    const doppelpunkt = zeile.indexOf(":");
    const feld = doppelpunkt === -1 ? zeile : zeile.slice(0, doppelpunkt);
    let wert = doppelpunkt === -1 ? "" : zeile.slice(doppelpunkt + 1);
    if (wert.startsWith(" ")) wert = wert.slice(1);
    if (feld === "data") daten.push(wert);
    else if (feld === "id") id = wert;
    else if (feld === "event") event = wert;
  }
  return { id: id || null, event: event || null, daten };
}

/** Die Nutzlast-Pruefung von `zerlegeSseBlock` (Begruendung dort). */
function ereignisAus(datenzeilen: string[]): SseEreignis | null {
  if (datenzeilen.length === 0) return null;

  let roh: unknown;
  try {
    roh = JSON.parse(datenzeilen.join("\n"));
  } catch { // still: ein fehlgeformter Ereignisblock wird verworfen (siehe oben) - er darf den Strom nicht abreissen
    return null;
  }
  if (typeof roh !== "object" || roh === null) return null;

  // Destrukturierung statt indizierten Zugriffs mit einem Namen aus Fremddaten - die fuenf Namen
  // stehen hier fest im Quelltext, es gibt also keinen datengetriebenen Schluessel, der auf
  // Object.prototype durchgreifen koennte.
  const { entity, id, rowVersion, actor, orderId } = roh as Record<string, unknown>;
  if (
    typeof entity !== "string" ||
    typeof id !== "string" ||
    typeof rowVersion !== "number" ||
    typeof actor !== "string" ||
    (orderId !== undefined && typeof orderId !== "string")
  ) {
    return null;
  }
  // orderId nur setzen, wenn es wirklich da war (Nutzdatenfreiheit-Stil wie events.ts, "orderId in
  // event") - ein `orderId: undefined` bei den uebrigen sechs Entitaeten waere ein Feld ohne Sinn.
  return orderId === undefined
    ? { entity, id, rowVersion, actor }
    : { entity, id, rowVersion, actor, orderId };
}

/**
 * ================= EINE VERBINDUNG FUER ALLE TABS (2026-08-15) =================
 *
 * DER VORFALL: Der Hub lud in mehreren offenen Tabs "einfach ewig" - kein Fehler, keine
 * Konsolenmeldung, nichts im Netzwerk-Tab ausser haengenden Anfragen. Der Server war dabei
 * nachweislich in Ordnung (alle Routen 200, die Bestellliste in 122 ms ueber Apache).
 *
 * DIE URSACHE liegt im Browser, nicht im Code: bei HTTP/1.1 laesst Chrome nur SECHS gleichzeitige
 * Verbindungen je Host zu. Jeder offene Tab haelt hier eine davon DAUERHAFT belegt - ein
 * SSE-Strom ist definitionsgemaess eine Verbindung, die nie endet. Ab dem sechsten Tab war der
 * Vorrat aufgebraucht, und jede weitere Anfrage wartete unbegrenzt auf einen freien Platz.
 *
 * Der Server laeuft seit dem 2026-08-15 mit HTTP/2 (Apache, mpm_event + mod_http2), womit das
 * Sechser-Limit entfaellt - alle Anfragen teilen sich eine Verbindung. Das ist die Behandlung des
 * Symptoms. Die Ursache ist, dass N Tabs N Stroeme oeffnen, obwohl EINER genuegt: sie tragen alle
 * denselben Inhalt. Serverseitig belegt jeder Strom weiterhin einen Thread (MaxRequestWorkers 150).
 *
 * DIE LOESUNG: Genau ein Tab haelt den Strom und verteilt die Ereignisse ueber einen
 * `BroadcastChannel` an die uebrigen. Die Wahl laeuft ueber die Web-Locks-API - `navigator.locks`
 * gibt eine Sperre automatisch an den naechsten Wartenden weiter, sobald ihr Halter verschwindet
 * (Tab geschlossen, abgestuerzt, Rechner zugeklappt). Genau dieses Verhalten braucht eine
 * Anfuehrerwahl, und es ist der Grund, warum hier keine Herzschlag-Loesung ueber `localStorage`
 * steht: die muesste den Ausfall selbst erkennen und haette dabei immer ein Zeitfenster, in dem
 * niemand zuhoert.
 *
 * OHNE die beiden Schnittstellen (alter Browser, Testumgebung) verhaelt sich alles wie zuvor: jeder
 * Aufrufer oeffnet seinen eigenen Strom. Der Rueckfall ist bewusst der bisherige Zustand und kein
 * Fehler - die Anwendung funktioniert dann wie bis zum 2026-08-15, nur eben mit N Verbindungen.
 */
const SPERRE_NAME = "absina-hub-sse";
const KANAL_NAME = "absina-hub-sse";

/**
 * Was ueber den Kanal laeuft. Der Anfuehrer reicht Ereignis, Stand und Luecke so weiter, wie sie im
 * Strom standen (`SseMeldung`); `luecke` ohne Nummer ist die Luecke, die er selbst angenommen hat
 * (Wiederaufbau ohne bekannte Nummer). `standFrage` stellt ein neuer Mithoerer, der Anfuehrer
 * antwortet darauf mit `stand`. `verbunden` meldet jeden erfolgreichen Aufbau des Anfuehrers; die Marke
 * `nummern` tragen nur Anfuehrer mit diesem Code (siehe `kanal.onmessage`).
 */
type KanalNachricht =
  | SseMeldung
  | { art: "luecke"; nummer: null }
  | { art: "standFrage" }
  | { art: "verbunden"; nummern?: true };

function kanalOeffnen(): BroadcastChannel | null {
  return typeof BroadcastChannel === "function" ? new BroadcastChannel(KANAL_NAME) : null;
}

function sperreVerfuegbar(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.locks?.request === "function";
}

export interface AbonnementOptionen {
  pfad?: string;
  /** Austauschbar fuer Tests; Vorgabe ist das globale `fetch`. */
  fetchImpl?: typeof fetch;
  /**
   * Schaltet die Anfuehrerwahl ab - dann oeffnet dieser Aufruf IMMER einen eigenen Strom. Fuer
   * Tests, die den Verbindungsaufbau selbst pruefen und keine Sperre gegen andere Testfaelle
   * halten sollen (eine Web-Lock gilt fuer den ganzen Ursprung, also auch ueber Testdateien hinweg).
   */
  ohneAnfuehrerwahl?: boolean;
  /**
   * Wird bei jedem Verbindungsabbruch gerufen (Diagnose, Verbindungsanzeige). Seit Befund B40 traegt
   * er bei einem 401 einen `SitzungsFehler` (`basis.ts`) statt eines schlichten `Error` - der
   * Aufrufer kann daran den Sitzungsverlust erkennen, ohne den HTTP-Status selbst nachzuhalten.
   */
  aufFehler?: (fehler: unknown) => void;
  /**
   * Wird nach JEDEM erfolgreichen (Wieder-)Verbindungsaufbau gerufen - beim ersten ebenso wie nach
   * einem Abbruch. Er sagt nur "verbunden": ob dabei etwas verloren ging, sagt `aufLuecke`; was der
   * Server nachliefern kann, kommt ueber `aufEreignis` (siehe Kopfkommentar).
   */
  aufVerbindung?: () => void;
  /**
   * Wird gerufen, wenn Ereignisse verloren sein koennen, die niemand nachliefert. Ausloeser:
   *   - der Server meldet `luecke` (Neustart, Puffer zu klein, zu viel nachzuliefern);
   *   - der Client baut ohne die Nummer neu auf, mit der die Anfrage haette gehen sollen (nach einer ersten
   *     Verbindung bzw. als uebernehmender Tab);
   *   - ein alter Server (Rueckrollen) nummeriert nicht, kennt keinen Stand und liefert nichts nach. Der Client
   *     erkennt ihn auf einem Wiederaufbau am ersten Block vor dem Stand, der keine Nummer traegt (Herzschlag,
   *     Ereignis ohne `id:`), und meldet die Luecke sofort; nach einer Verbindung, die 200 bekam, aber weder
   *     Stand noch Luecke brachte, beim naechsten Aufbau;
   *   - ein Anfuehrer mit altem Buendel meldet `verbunden` ohne Marke (nur ueber den Kanal).
   * Die ERSTE Verbindung ohne Nummer ist keine Luecke - den Bestand laedt der Erstlade-Effekt.
   * Mithoerer-Tabs bekommen den Aufruf ebenfalls, vom Anfuehrer ueber den Kanal.
   */
  aufLuecke?: () => void;
}

/**
 * Abonniert den Ereignisstrom. Liefert die Abbestellfunktion - sie bricht eine laufende Verbindung
 * ab UND verhindert jeden weiteren Wiederaufbau. Ohne ihren Aufruf laeuft die Wiederverbindungs-
 * schleife fuer immer weiter.
 */
export function ereignisseAbonnieren(
  aufEreignis: (ereignis: SseEreignis) => void,
  optionen: AbonnementOptionen = {},
): () => void {
  const {
    pfad = "/api/events",
    fetchImpl = ((eingabe, init) => fetch(eingabe, init)) as typeof fetch,
    aufFehler,
    aufVerbindung,
    aufLuecke,
    ohneAnfuehrerwahl = false,
  } = optionen;

  let abgebrochen = false;
  let steuerung: AbortController | null = null;
  let wiederholungsWecker: ReturnType<typeof setTimeout> | null = null;
  let fehlversuche = 0;
  /** Nur der Anfuehrer haelt den Strom - siehe Anfuehrerwahl weiter unten. */
  let istAnfuehrer = false;
  let sperreFreigeben: (() => void) | null = null;
  /**
   * Die Nummer der zuletzt gesehenen Meldung - aus dem eigenen Strom oder vom Kanal. Sie geht bei
   * jedem Aufbau als `last-event-id` mit; `null`, solange keine Meldung eine getragen hat.
   */
  let letzteNummer: string | null = null;
  /** Ob dieses Abonnement schon einmal HTTP 200 bekam; Fehlversuche davor zaehlen nicht. */
  let hatteVerbindung = false;
  /** Ob dieser Tab die Sperre von einem anderen uebernommen hat (und nicht als Erster hielt). */
  let uebernahme = false;
  /**
   * Ob die letzte Verbindung HTTP 200 bekam und seither weder Stand noch Luecke kam. Der neue Server schickt
   * eines von beiden sofort nach dem 200; ein alter (Rueckrollen) nie, und seine Ereignisse tragen keine
   * Nummer - `letzteNummer` bliebe stehen und taeuschte einen lueckenlosen Strom vor. Meldet der Aufbau danach
   * die Luecke, gilt das nur fuer ihn; `frueh` holt es auf der Verbindung selbst nach.
   */
  let ohneStand = false;
  /**
   * Ob diese Verbindung ein Wiederaufbau ist, der noch keine Luecke gemeldet hat und noch keinen Stand sah. Der
   * erste Block ohne Nummer (Herzschlag, Ereignis ohne `id:`) verraet dann den alten Server: der neue schreibt vor
   * dem Stand nur nachgelieferte Ereignisse MIT Nummer. Die erste Verbindung eines Abonnements meldet nie.
   */
  let frueh = false;

  async function stromLesen(koerper: ReadableStream<Uint8Array>): Promise<void> {
    const leser = koerper.getReader();
    const dekoder = new TextDecoder();
    let puffer = "";
    let wache: ReturnType<typeof setTimeout> | null = null;

    const wacheStellen = () => {
      if (wache) clearTimeout(wache);
      // Kommt HEARTBEAT_TOLERANZ_MS lang kein einziges Byte, gilt die Verbindung als tot. Ein
      // abgerissenes TCP kann sonst beliebig lange als "offen" dastehen, ohne dass `read()` je
      // zurueckkehrt - der Client bekaeme dann nie wieder ein Ereignis und merkte es nicht.
      wache = setTimeout(() => steuerung?.abort(), HEARTBEAT_TOLERANZ_MS);
    };

    wacheStellen();
    try {
      for (;;) {
        const { value, done } = await leser.read();
        if (done) break;
        wacheStellen();
        puffer += dekoder.decode(value, { stream: true });
        puffer = puffer.replace(/\r\n/g, "\n");
        let grenze = puffer.indexOf("\n\n");
        while (grenze !== -1) {
          const block = puffer.slice(0, grenze);
          puffer = puffer.slice(grenze + 2);
          const meldung = zerlegeSseMeldung(block);
          if (frueh && !meldung?.nummer) {
            frueh = false;
            lueckeMelden();
          }
          if (meldung) {
            // Die Nummer jeder Meldung, die eine traegt, wird gemerkt - VOR dem Rueckruf: ein Ereignis,
            // das der Rueckruf nicht verdaut, wird nicht bei jedem Wiederaufbau erneut nachgeliefert.
            if (meldung.nummer) letzteNummer = meldung.nummer;
            if (meldung.art !== "ereignis") ohneStand = frueh = false;
            if (meldung.art === "ereignis") aufEreignis(meldung.ereignis);
            else if (meldung.art === "luecke") aufLuecke?.();
            // Der Anfuehrer versorgt die uebrigen Tabs: Ereignis, Stand und Luecke samt Nummer.
            senden(meldung);
          }
          grenze = puffer.indexOf("\n\n");
        }
      }
    } finally {
      if (wache) clearTimeout(wache);
      leser.releaseLock();
    }
  }

  async function verbinden(): Promise<void> {
    if (abgebrochen) return;
    steuerung = new AbortController();
    // Die Nummer, mit der DIESE Anfrage geht: die Luecke unten richtet sich nach ihr. Ueber den Kanal kann
    // `letzteNummer` sich bis zur Antwort aendern, die Anfrage hat davon nichts mehr.
    const gesendet = letzteNummer;
    try {
      const antwort = await fetchImpl(pfad, {
        // standardKopfzeilen setzt Authorization und x-client-id - genau die zwei, die
        // `EventSource` nicht mitgeben koennte (siehe Kopfkommentar). Der Aufbau (und jeder
        // Wiederaufbau) ist nie eine Handlung des Menschen: `hintergrund = true`, immer - auch im
        // sichtbaren Tab - damit ein nur offener Tab nicht als "aktiv" zaehlt. `last-event-id`
        // setzt hier der Client selbst, was `EventSource` beim Wiederverbinden sonst taete.
        headers: {
          ...standardKopfzeilen(false, true),
          accept: "text/event-stream",
          ...(gesendet === null ? {} : { "last-event-id": gesendet }),
        },
        signal: steuerung.signal,
      });
      if (!antwort.ok) {
        if (antwort.status === 401) {
          // Befund B40 (Nachmessung R5): ein 401 auf dem Strom ist derselbe Sitzungsverlust wie
          // ein 401 auf jeder anderen Anfrage (`anfrage()` in `basis.ts`) - deshalb hier dasselbe
          // Aufraeumen (Token samt `localStorage['hubSession']`, `setzeSitzungsToken()` tut
          // beides) und derselbe Fehlertyp. `anfrage()` selbst kann hier nicht greifen: der Strom
          // laeuft ueber ein eigenes `fetch`, nicht darueber. Der Store (`state/Speicher.tsx`)
          // erkennt `SitzungsFehler` in seinem `aufFehler` und meldet den Nutzer ab - derselbe Weg
          // wie bei jedem anderen 401, nur ueber den Store statt ueber `beiSitzungsverlust`.
          setzeSitzungsToken(null);
          throw new SitzungsFehler(`SSE: HTTP ${antwort.status}`, undefined);
        }
        throw new Error(`SSE: HTTP ${antwort.status}`);
      }
      if (!antwort.body) throw new Error("SSE: Antwort ohne Koerper");
      fehlversuche = 0;
      aufVerbindung?.();
      // Auch die Mithoerer erfahren den Aufbau: der Anfuehrer ist ihre einzige Quelle.
      senden({ art: "verbunden", nummern: true });
      // Ohne Nummer weiss der Server nichts, was er nachliefern koennte. War diese Verbindung nicht
      // die erste (oder hat dieser Tab die Sperre uebernommen), fehlt dann womoeglich Verpasstes:
      // Luecke, fuer alle Tabs. Die erste Verbindung ist keine - den Bestand laedt der Erstlade-Effekt.
      // Ebenso, wenn die Verbindung davor ohne Stand und ohne Luecke blieb: dort sprach ein alter Server.
      // Ein Wiederaufbau, der hier noch nichts meldet, tut es im Strom beim ersten Block ohne Nummer (`frueh`).
      const wieder = hatteVerbindung || uebernahme;
      const lueckig = (gesendet === null && wieder) || ohneStand;
      if (lueckig) lueckeMelden();
      frueh = wieder && !lueckig;
      hatteVerbindung = true;
      ohneStand = true; // bis Stand oder Luecke dieser Verbindung kommen
      await stromLesen(antwort.body);
    } catch (fehler) {
      if (abgebrochen) return;
      aufFehler?.(fehler);
    }
    if (abgebrochen) return;
    fehlversuche++;
    wiederholungsWecker = setTimeout(() => {
      void verbinden();
    }, naechsterAbstand(fehlversuche));
  }

  // ============================ Anfuehrerwahl (siehe Kopfkommentar) ============================

  /**
   * Der Kanal haengt an der SPERRE, nicht nur am eigenen Vorhandensein - beides zusammen oder
   * keines von beiden.
   *
   * Gaebe es den Kanal ohne die Sperre (BroadcastChannel vorhanden, `navigator.locks` nicht - genau
   * die Lage in jsdom), liefe der Rueckfall: JEDER Abonnent oeffnet seinen eigenen Strom. Er wuerde
   * dann aber ZUSAETZLICH auf dem Kanal mithoeren und jedes Ereignis doppelt verarbeiten - einmal
   * aus dem eigenen Strom, einmal vom Nachbarn. In der Testsuite hat sich das als Interferenz
   * zwischen Testdateien gezeigt (zwei Dateien wurden im Gesamtlauf rot, einzeln blieben sie gruen).
   */
  const anfuehrerwahlMoeglich = !ohneAnfuehrerwahl && sperreVerfuegbar();
  const kanal = anfuehrerwahlMoeglich ? kanalOeffnen() : null;

  /**
   * `postMessage` kann werfen, wenn der Kanal zwischenzeitlich geschlossen wurde (Abbestellung
   * waehrend eines laufenden Lesevorgangs) - das darf den Strom nicht abreissen lassen, der fuer
   * DIESEN Tab weiterhin richtig ist. Verloren geht dabei nur, was die uebrigen Tabs erfahren sollten;
   * dieser Tab hat es ueber seine eigenen Rueckrufe schon (oder braucht es nicht: `standFrage`).
   */
  const senden = (nachricht: KanalNachricht) => {
    try {
      kanal?.postMessage(nachricht);
    } catch { // still: Kanal zu - nur die uebrigen Tabs erfahren die Nachricht nicht mehr, dieser Tab ist nicht betroffen
    }
  };

  /** Die Luecke, die dieser Tab selbst annimmt: fuer ihn und, ohne Nummer, fuer alle Tabs. */
  const lueckeMelden = () => {
    aufLuecke?.();
    senden({ art: "luecke", nummer: null });
  };

  /**
   * Die Mithoerer. Sie oeffnen KEINE Verbindung; was der Anfuehrer empfaengt, kommt hier an: Ereignisse,
   * die Luecke und jeder Aufbau (`aufVerbindung`) - sonst haetten sie eine Luecke, die nur der
   * Anfuehrer sieht. Jede Nummer wird gemerkt: uebernimmt dieser Tab die Sperre, setzt er damit auf.
   */
  if (kanal) {
    kanal.onmessage = (nachricht: MessageEvent<KanalNachricht>) => {
      if (abgebrochen) return;
      const inhalt = nachricht.data;
      if (!inhalt) return;
      if ("nummer" in inhalt && inhalt.nummer) letzteNummer = inhalt.nummer;
      if (inhalt.art === "ereignis") aufEreignis(inhalt.ereignis);
      else if (inhalt.art === "luecke") aufLuecke?.();
      else if (inhalt.art === "verbunden") {
        aufVerbindung?.();
        // Ein `verbunden` OHNE Marke kommt von einem Tab mit altem Buendel als Anfuehrer (alt und neu
        // teilen Sperre und Kanal): der hat sich gerade neu verbunden, kennt aber keine Nummern und
        // liefert nichts nach - fuer diesen Tab ist das eine Luecke.
        if (!inhalt.nummern) aufLuecke?.();
      }
      // Beitritt: ein neuer Mithoerer kennt noch keine Nummer, und in ruhiger Zeit kaeme keine mehr.
      else if (inhalt.art === "standFrage" && istAnfuehrer && letzteNummer) {
        senden({ art: "stand", nummer: letzteNummer });
      }
    };
  }

  /**
   * Der Anfuehrer reicht weiter, was er empfaengt. Die eigene Rueckmeldung laeuft dabei WEITER ueber
   * die urspruenglichen Rueckrufe und NICHT ueber den Kanal - ein `BroadcastChannel` stellt dem
   * Absender seine eigenen Nachrichten nicht zu, und ihn hier zusaetzlich abzuhoeren waere ein
   * zweiter Weg fuer dieselbe Sache.
   */
  const alsAnfuehrerStarten = () => {
    if (abgebrochen) return;
    istAnfuehrer = true;
    void verbinden();
  };

  if (anfuehrerwahlMoeglich && kanal) {
    // Die Sperre wird gehalten, solange dieses Versprechen offen ist - also bis zur Abbestellung.
    // Genau so ist die Web-Locks-API gedacht: der Halter gibt sie zurueck, indem er auflöst.
    sperreFreigeben = () => {};
    const gehalten = new Promise<void>((aufloesen) => {
      sperreFreigeben = aufloesen;
    });
    // Zweistufig: erst nur, wenn die Sperre FREI ist. Ist sie es nicht, haelt ein anderer Tab den Strom
    // - dann fragt dieser Tab ihn nach der aktuellen Nummer (Beitritt) und wartet auf die Sperre. Eine
    // Sperre aus dem Warten ist eine UEBERNAHME: der Vorgaenger ist verschwunden.
    void navigator.locks.request(SPERRE_NAME, { ifAvailable: true }, (sperre) => {
      if (sperre) {
        alsAnfuehrerStarten();
        return gehalten;
      }
      senden({ art: "standFrage" });
      return navigator.locks.request(SPERRE_NAME, () => {
        uebernahme = true;
        alsAnfuehrerStarten();
        return gehalten;
      });
    });
  } else {
    // Kein BroadcastChannel oder keine Web-Locks: Verhalten wie bis zum 2026-08-15.
    alsAnfuehrerStarten();
  }

  return () => {
    abgebrochen = true;
    if (wiederholungsWecker) clearTimeout(wiederholungsWecker);
    steuerung?.abort();
    // Die Sperre freigeben, BEVOR der Kanal zugeht: der naechste wartende Tab uebernimmt damit
    // sofort. Andersherum bliebe sie bis zum Einsammeln durch den Browser liegen.
    sperreFreigeben?.();
    kanal?.close();
  };
}
